import { strictify } from '~/utils/strictify'
import { z } from 'zod'
import type { VerifyEmailParams } from './VerifyEmailParams'
import {
  VerificationCheckSuccessSchema,
  VerificationResendSuccessSchema,
  VerificationVerifyPathSchema,
  VerificationVerifyQuerySchema,
  VerificationVerifySuccessSchema
} from '~/services/api/generated'

const positiveSafeIntegerSchema = z.number().int().positive()

const toPositiveSafeIntegerSchema = z.union([
  positiveSafeIntegerSchema,
  z
    .string()
    .regex(/^[1-9]\d*$/u)
    .transform((value) => Number(value))
    .pipe(positiveSafeIntegerSchema)
])

// The generated `VerificationVerifyPathSchema.hash` lost its format constraint
// (bare `z.string()`); re-validated here since it's a real Laravel signed-URL
// hash, always a 40-character lowercase hex SHA-1.
const verificationVerifyPathSchema = VerificationVerifyPathSchema.extend({
  hash: z.string().regex(/^[a-f0-9]{40}$/)
}).strict()

export function useEmailService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/email`
  const sanctumClient = useApiRequest()

  /**
   * Resend email verification notification
   */
  async function resendVerification() {
    return strictify(VerificationResendSuccessSchema).parse(
      await sanctumClient(`${baseURL}/verification-notification`, {
        method: 'POST'
      })
    )
  }

  /**
   * Verify email with the parameters from the verification link
   */
  async function verifyEmail(params: VerifyEmailParams) {
    const path = verificationVerifyPathSchema.parse({
      hash: params.hash,
      id: toPositiveSafeIntegerSchema.parse(params.id)
    })
    const query = VerificationVerifyQuerySchema.strict().parse({
      expires: toPositiveSafeIntegerSchema.parse(params.expires),
      signature: params.signature
    })
    return strictify(VerificationVerifySuccessSchema).parse(
      await sanctumClient(
        `${baseURL}/verify/${encodeURIComponent(path.id.toString())}/${encodeURIComponent(path.hash)}`,
        {
          method: 'GET',
          query,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      )
    )
  }

  /**
   * Check the current email verification status
   */
  async function checkVerificationStatus() {
    return strictify(VerificationCheckSuccessSchema).parse(
      await sanctumClient(`${baseURL}/verification-status`, { method: 'GET' })
    )
  }

  return {
    resendVerification,
    verifyEmail,
    checkVerificationStatus
  }
}
